/**
 * apiFetch - Production-Safe API Helper
 * date_of_change: 2025-09-26
 *
 * Provides consistent API calls with:
 * - Production-safe backend URL resolution (pins to Railway in production)
 * - Clerk JWT token authentication (default session token)
 * - Credentials included for auth/session
 * - CORS handling
 * - Error handling
 */

import { getBackendBase } from './BackendUrlResolver';
import { DEBUG_ENABLED } from './DebugLogger';

export async function apiFetch(path, options = {}) {
  const debugLog = (...args) => {
    if (DEBUG_ENABLED) {
      console.debug(...args);
    }
  };
  const base = await getBackendBase();

  // @date_of_change: 2025-09-21 - Fix double /api issue and credentials handling
  let url;
  if (path.startsWith('http')) {
    url = path;
  } else {
    // Ensure we don't double up /api in the URL
    const cleanBase = base.endsWith('/api') ? base.slice(0, -4) : base;
    url = `${cleanBase}${path}`;
  }

  // @date_of_change: 2025-10-06 - Robust Clerk auth: wait for session, try default token, then fall back to 'backend' template
  let token = null;
  let used = 'none';

  // Debug logging to prove frontend is using new code
  debugLog('[apiFetch] Clerk session present?', !!window.Clerk?.session);

  // Small helper to wait for Clerk session to be available
  const waitForClerkSession = async (tries = 5, delayMs = 200) => {
    for (let i = 0; i < tries; i++) {
      const s = window.Clerk?.session;
      if (s) return s;
      if (typeof window.Clerk?.load === 'function') {
        try { await window.Clerk.load(); } catch (_) {}
      }
      await new Promise(r => setTimeout(r, delayMs));
    }
    return window.Clerk?.session || null;
  };
  
  try {
    const session = await waitForClerkSession();
    if (session) {
      // Attempt default token first
      try {
        token = await session.getToken();
        used = 'default';
      } catch (e1) {
        console.warn('[apiFetch] Default token fetch failed:', e1?.message);
      }
      
      // Fallback: some backends require a specific template (e.g., 'backend')
      if (!token) {
        try {
          token = await session.getToken({ template: 'backend' });
          if (token) used = 'backend-template';
        } catch (e2) {
          console.warn('[apiFetch] Backend template token fetch failed:', e2?.message);
        }
      }
      
      if (token) {
        debugLog('[apiFetch] Acquired Clerk token (mode:', used + '), length:', token.length);
      } else {
        console.warn('[apiFetch] No Clerk token available after retries');
      }
    } else {
      console.warn('[apiFetch] No Clerk session available for token acquisition');
    }
    
    debugLog('[apiFetch] Token acquisition result:', { used, hasToken: !!token });
  } catch (error) {
    console.warn('[apiFetch] Clerk token acquisition error (continuing without token):', error?.message);
  }

  // @date_of_change: 2025-09-21 - Use 'same-origin' for development to avoid CORS wildcard issue
  const credentials = url.includes('localhost') || url.includes('127.0.0.1') ? 'same-origin' : 'include';

  // @date_of_change: 2025-10-05 - Skip Content-Type for FormData to allow browser to set multipart boundary
  const isFormData = options?.body instanceof FormData;

  // Merge caller headers into ours, then ensure fetch doesn't override by spreading options without headers below
  const optHeaders = (options && options.headers) || {};
  const headers = {
    // Only set Content-Type for JSON requests, not FormData (browser handles multipart/form-data boundary)
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'ngrok-skip-browser-warning': 'true',
    ...optHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  // Log whether Authorization header is present
  if (!headers.Authorization) {
    console.error('[apiFetch] WARNING: No Authorization header set for request to:', path);
  } else {
    debugLog('[apiFetch] Authorization header set, token preview:', headers.Authorization.substring(0, 20) + '...');
  }

  // date_of_change: 2025-11-30 - Enforce optional auth gating for protected endpoints per AGENTS.md (no silent fallbacks)
  // When options.requireAuth is true and no token is available, do not issue the request at all.
  if (options && options.requireAuth && !headers.Authorization) {
    throw new Error('[apiFetch] Auth required but no token available');
  }

  // Prevent options.headers from clobbering our composed headers
  const { headers: _ignored, ...rest } = options || {};
  return fetch(url, {
    credentials,    // avoid wildcard CORS issue in development
    mode: 'cors',
    headers,
    ...rest
  });
}

export default apiFetch;
